/**
 * Data helpers for the AI Clipper post admin surface. JWT flows through
 * the app Apollo client's authLink (configured in App.tsx). All resolvers are
 * admin-only on the backend.
 */
import { ApolloClient } from '@apollo/client';
import {
  ACCEPT_POST_AI_CLIP_CANDIDATE,
  CREATE_PRESIGNED_AI_CLIPPER_CHANNEL_LOGO_URL,
  GET_AI_CLIPPER_SETTINGS,
  GET_AI_CLIPPER_USER_ACCESS,
  GET_AI_CLIPPER_USER_ACCESS_LIST,
  GET_POST_AI_CLIP_CANDIDATES,
  REJECT_POST_AI_CLIP_CANDIDATE,
  SET_AI_CLIPPER_SETTINGS,
  SET_AI_CLIPPER_USER_ACCESS,
} from './queries';
import { AiClipperSettings } from './types/AiClipperSettings';
import { AiClipperUserAccess as AiClipperUserAccessQuery } from './types/AiClipperUserAccess';
import { AiClipperUserAccessList } from './types/AiClipperUserAccessList';
import { GetPostAiClipCandidates } from './types/GetPostAiClipCandidates';
import { AcceptPostAiClipCandidate } from './types/AcceptPostAiClipCandidate';
import { RejectPostAiClipCandidate } from './types/RejectPostAiClipCandidate';
import { SetAiClipperSettings } from './types/SetAiClipperSettings';
import {
  AcceptPostAiClipCandidateInput,
  AiClipperSetting,
  AiClipperUserAccess,
  AiClipperUserAccessListInput,
  AiClipperUserAccessPage,
  CreatePresignedAiClipperChannelLogoUrlInput,
  GetPostAiClipCandidatesInput,
  PostAiClipCandidate,
  PostAiClipCandidatePage,
  SetAiClipperSettingsInput,
  SetAiClipperUserAccessInput,
} from './types';

type AppApolloClient = ApolloClient<any>;
const POST_CANDIDATES_PAGE_SIZE = 100;

export async function fetchAiClipperSettings(
  client: AppApolloClient
): Promise<AiClipperSetting[]> {
  const result = await client.query<AiClipperSettings>({
    query: GET_AI_CLIPPER_SETTINGS,
    fetchPolicy: 'network-only',
  });
  return result.data?.aiClipperSettings ?? [];
}

export async function setAiClipperSettings(
  client: AppApolloClient,
  input: SetAiClipperSettingsInput
): Promise<AiClipperSetting[]> {
  const result = await client.mutate<SetAiClipperSettings>({
    mutation: SET_AI_CLIPPER_SETTINGS,
    variables: { input },
  });
  return result.data?.setAiClipperSettings ?? [];
}

export async function fetchAiClipperUserAccess(
  client: AppApolloClient,
  userId: string
): Promise<AiClipperUserAccess | null> {
  const result = await client.query<AiClipperUserAccessQuery>({
    query: GET_AI_CLIPPER_USER_ACCESS,
    variables: { userId },
    fetchPolicy: 'network-only',
  });
  return result.data?.aiClipperUserAccess ?? null;
}

export async function fetchAiClipperUserAccessList(
  client: AppApolloClient,
  input: AiClipperUserAccessListInput
): Promise<AiClipperUserAccessPage> {
  const result = await client.query<AiClipperUserAccessList>({
    query: GET_AI_CLIPPER_USER_ACCESS_LIST,
    variables: { input },
    fetchPolicy: 'network-only',
  });
  return (
    result.data?.aiClipperUserAccessList ?? {
      __typename: 'AiClipperUserAccessPage',
      entities: [],
      total: 0,
      offset: input.offset ?? 0,
      limit: input.limit ?? 20,
    }
  );
}

export async function setAiClipperUserAccess(
  client: AppApolloClient,
  input: SetAiClipperUserAccessInput
): Promise<AiClipperUserAccess | null> {
  const result = await client.mutate<{
    setAiClipperUserAccess: AiClipperUserAccess;
  }>({
    mutation: SET_AI_CLIPPER_USER_ACCESS,
    variables: { input },
  });
  return result.data?.setAiClipperUserAccess ?? null;
}

export async function createPresignedAiClipperChannelLogoUrl(
  client: AppApolloClient,
  input: CreatePresignedAiClipperChannelLogoUrlInput
): Promise<{ key: string; url: string; fields: string }> {
  const result = await client.mutate<{
    createPresignedAiClipperChannelLogoUrl: {
      key: string;
      url: string;
      fields: string;
    };
  }>({
    mutation: CREATE_PRESIGNED_AI_CLIPPER_CHANNEL_LOGO_URL,
    variables: { input },
  });
  const data = result.data?.createPresignedAiClipperChannelLogoUrl;
  if (!data) throw new Error('Failed to get presigned URL');
  return data;
}

export async function fetchPostCandidates(
  client: AppApolloClient,
  input: GetPostAiClipCandidatesInput
): Promise<PostAiClipCandidatePage> {
  const result = await client.query<GetPostAiClipCandidates>({
    query: GET_POST_AI_CLIP_CANDIDATES,
    variables: { input },
    fetchPolicy: 'network-only',
  });
  return (
    result.data?.getPostAiClipCandidates ?? {
      __typename: 'PostAiClipCandidatePage',
      entities: [],
      total: 0,
      offset: input.offset ?? 0,
      limit: input.limit ?? 20,
    }
  );
}

export async function fetchAllPostCandidates(
  client: AppApolloClient,
  input: GetPostAiClipCandidatesInput
): Promise<PostAiClipCandidate[]> {
  const limit = input.limit ?? POST_CANDIDATES_PAGE_SIZE;
  let offset = input.offset ?? 0;
  const entities: PostAiClipCandidate[] = [];

  let hasMore = true;
  while (hasMore) {
    const page = await fetchPostCandidates(client, {
      ...input,
      limit,
      offset,
    });

    entities.push(...page.entities);

    hasMore =
      page.entities.length > 0 &&
      entities.length < page.total &&
      page.entities.length >= limit;

    if (!hasMore) {
      return entities;
    }

    offset += limit;
  }

  return entities;
}

export async function acceptPostCandidate(
  client: AppApolloClient,
  input: AcceptPostAiClipCandidateInput
): Promise<PostAiClipCandidate | null> {
  const result = await client.mutate<AcceptPostAiClipCandidate>({
    mutation: ACCEPT_POST_AI_CLIP_CANDIDATE,
    variables: { input },
  });
  return result.data?.acceptPostAiClipCandidate ?? null;
}

export async function rejectPostCandidate(
  client: AppApolloClient,
  candidateId: string
): Promise<PostAiClipCandidate | null> {
  const result = await client.mutate<RejectPostAiClipCandidate>({
    mutation: REJECT_POST_AI_CLIP_CANDIDATE,
    variables: { input: { candidateId } },
  });
  return result.data?.rejectPostAiClipCandidate ?? null;
}
